import { ResizeObserver } from '@juggle/resize-observer';
import classNames from 'classnames';
import debounce from 'debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';

import { generalHelpers } from '../../helpers';
import TooltipWrapper from '../general/TooltipWrapper';

const HiddenOptionContainer = styled.div`
  background: white;
  border: solid 1px #888;
  z-index: 2;
  min-width: 100px;

  &[data-visible='false'] {
    display: none;
  }

  .hidden-option {
    padding: 2px 5px;
    color: #444;
    cursor: pointer;
    &:not(:first-child) {
      border-top: solid 1px #aaa;
    }
    &:hover {
      background-color: #ccc;
    }
  }
`;

interface Props<T> {
  name?: string;
  onChange?: (name: string, value: T) => void;
  onValueChange?: (value: T) => void;
  value: T;
  options: T[];
  labels?: any[];
  tooltips?: string[];
  prefix?: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const FlatSelect = function FlatSelect<T>({
  name,
  onChange,
  onValueChange,
  value,
  options,
  labels,
  tooltips,
  prefix,
}: Props<T>): React.ReactElement {
  const ref = useRef<HTMLDivElement>();
  const [popperReferenceRef, setPopperReferenceRef] = useState(null);
  const [popperRef, setPopperRef] = useState(null);

  const {
    styles,
    attributes,
    update: updatePopper,
  } = usePopper(popperReferenceRef, popperRef, {
    placement: 'bottom',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          tether: false,
        },
      },
    ],
  });

  const [hidden, setHidden] = useState(options.map(() => false));
  const [shouldLayout, setShouldLayout] = useState(false);
  const [showHiddenOptions, setShowHiddenOptions] = useState(false);

  const resetHidden = useCallback(
    () => setHidden(hidden => hidden.map(() => false)),
    []
  );

  const updateShouldLayout = useCallback(() => {
    const container = ref.current;
    if (!container) {
      return;
    }
    const elements = [...container.querySelectorAll('.dl-switch')]
      .filter(element => element instanceof HTMLElement)
      .map(element => element as HTMLElement);
    const offsetTops = elements.map(element => element.offsetTop);
    setShouldLayout(Math.max(...offsetTops) > Math.min(...offsetTops));
  }, []);

  useEffect(() => {
    const container = ref.current;
    const mutationObserver = new MutationObserver(updateShouldLayout);
    mutationObserver.observe(container, { childList: true });

    let prevWidth = 0;
    const onResize = debounce((entries: ResizeObserverEntry[]) => {
      if (entries.length === 1) {
        if (entries[0].contentRect.width > prevWidth) {
          // only reset the hidden (expand all options) when being enlarged.
          resetHidden();
        }
        prevWidth = entries[0].contentRect.width;
      }
      updateShouldLayout();
      setShowHiddenOptions(false);
    }, 200);

    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    updateShouldLayout();

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [resetHidden, updateShouldLayout]);

  useEffect(() => {
    // remove an element and set shouldLayout to false
    if (shouldLayout) {
      let index = hidden.length;
      do {
        index = hidden.lastIndexOf(false, index - 1);
      } while (value === options[index] && index > 0);
      setHidden(hidden => hidden.map((h, i) => (i === index ? true : h)));
      setShouldLayout(false);
      if (updatePopper) updatePopper();
    }
  }, [hidden, options, shouldLayout, updatePopper, value]);

  useEffect(() => {
    if (showHiddenOptions) {
      const onClick = () => setShowHiddenOptions(false);
      document.addEventListener('click', onClick);
      return () => document.removeEventListener('click', onClick);
    }
    return generalHelpers.noop;
  }, [showHiddenOptions]);

  const handleClick = useCallback(
    option => {
      onChange && onChange(name, option);
      onValueChange && onValueChange(option);
    },
    [name, onChange, onValueChange]
  );

  const _labels = labels || options;
  const _tooltips = tooltips || Array.from({ length: options.length });

  const hasHiddenOptions = hidden.some(Boolean);

  const handleToggleHiddenOptions: React.MouseEventHandler = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setShowHiddenOptions(show => !show);
  }, []);

  return (
    <div ref={ref} className="dl-switch-container">
      {prefix && <div className="dl-switch dl-switch-label">{prefix}</div>}
      {options.map((option, i) => {
        if (hidden[i]) {
          return null;
        }
        return (
          <TooltipWrapper key={String(option)} tooltip={_tooltips[i]}>
            <div
              className={'dl-switch' + (value === option ? ' checked' : '')}
              onClick={() => handleClick(option)}
            >
              {_labels[i]}
            </div>
          </TooltipWrapper>
        );
      })}
      {hasHiddenOptions && (
        <div
          ref={setPopperReferenceRef}
          className={classNames('dl-switch', { checked: showHiddenOptions })}
          onClick={handleToggleHiddenOptions}
        >
          ...
        </div>
      )}
      <HiddenOptionContainer
        ref={setPopperRef}
        style={styles.popper}
        {...attributes.popper}
        data-visible={showHiddenOptions}
      >
        {options.map((option, i) => {
          if (!hidden[i]) {
            return null;
          }
          return (
            <div
              className="hidden-option"
              key={i}
              onClick={() => {
                setShowHiddenOptions(false);
                handleClick(option);
                resetHidden();
              }}
            >
              {_labels[i]}
            </div>
          );
        })}
      </HiddenOptionContainer>
    </div>
  );
};

export default FlatSelect;
